import { NetworkCampaign } from '@common/types/campaign';

import { Store } from '@campaign/domain/store';
import { initialState } from '@campaign/domain/store/state';
import { State, PaginationState } from '@campaign/types';

/**
 * Mark app as initialized (public data + auth init has been started).
 * Used to ensure app-level init runs only once.
 */
export function setInitialized(store: Store): void {
  store.swap((state) => ({ ...state, initialized: true }), '@set-initialized');
}

/**
 * Update loading state
 * @param store
 * @param user
 */
export function updateLoading(
  store: Store,
  key: keyof State['loading'],
  value: boolean
) {
  store.swap(
    (state) => ({ ...state, loading: { ...state.loading, [key]: value } }),
    '@update-loading'
  );
}

/**
 * Set logged in user to state without token property
 * @param store
 * @param user
 */
export function setLoggedInUserWithoutToken(
  store: Store,
  loggedInUser: Omit<State['loggedInUser'], 'token'>
) {
  store.swap(
    (state) => ({
      ...state,
      loggedInUser: {
        ...loggedInUser,
        token: state.loggedInUser.token,
      },
    }),
    '@set-logged-in-user'
  );
}

/**
 * Clears logged-in user fields, access token, campaign lists, and tab for guest session.
 */
export function resetLoggedOutUserSession(store: Store): void {
  store.swap(
    (state) => ({
      ...state,
      campaignList: [],
      errors: {
        ...state.errors,
        campaignList: '',
        userCampaignList: '',
      },
      loading: {
        ...state.loading,
        campaignList: true,
        userCampaignList: false,
      },
      loggedInUser: initialState.loggedInUser,
      pagination: initialState.pagination,
      selectedCampaignTab: 0,
      userCampaignList: [],
    }),
    '@reset-logged-out-user-session'
  );
}

/**
 * Set access token in the state
 * @param store
 * @param user
 */
export function setRefreshAccessToken(
  store: Store,
  token: State['loggedInUser']['token']
) {
  store.swap(
    (state) => ({
      ...state,
      loggedInUser: {
        ...state.loggedInUser,
        token,
      },
    }),
    '@set-access-token'
  );
}

/**
 * Set selected campaign tab
 *
 * @param store
 * @param selectedCampaignTab
 */
export function setSelectedCampaignTab(
  store: Store,
  selectedCampaignTab: 0 | 1
) {
  store.swap(
    (state) => ({
      ...state,
      selectedCampaignTab,
    }),
    '@set-selected-campaign-tab'
  );
}

/**
 * Update pagination offset immediately so controls reflect the selected page
 * while the next page is loading
 */
export function updatePaginationOffset(
  store: Store,
  key: 'campaignList' | 'userCampaignList',
  offset: number
) {
  store.swap(
    (state) => ({
      ...state,
      pagination: {
        ...state.pagination,
        [key]: {
          ...state.pagination[key],
          offset,
        },
      },
    }),
    '@update-pagination-offset'
  );
}

/**
 * Update campaign list and pagination in a single store swap.
 */
export function updateCampaignListPage(
  store: Store,
  campaignList: NetworkCampaign[],
  pagination: PaginationState
) {
  store.swap(
    (state) => ({
      ...state,
      campaignList,
      errors: {
        ...state.errors,
        campaignList: '',
      },
      pagination: {
        ...state.pagination,
        campaignList: pagination,
      },
    }),
    '@update-campaign-list-page'
  );
}

/**
 * Update user campaign list and pagination in a single store swap.
 */
export function updateUserCampaignListPage(
  store: Store,
  userCampaignList: NetworkCampaign[],
  pagination: PaginationState
) {
  store.swap(
    (state) => ({
      ...state,
      errors: {
        ...state.errors,
        userCampaignList: '',
      },
      pagination: {
        ...state.pagination,
        userCampaignList: pagination,
      },
      userCampaignList,
    }),
    '@update-user-campaign-list-page'
  );
}

/**
 * updated error modal state
 * @param store - js-atom instance
 * @param isOpen open/close the modal
 * @param errorCode a code used in the component to show different errors
 */
export function setErrorModal(
  store: Store,
  isModalOpen: boolean,
  errorCode: string
) {
  store.swap(
    (state) => ({
      ...state,
      errors: {
        ...state.errors,
        modal: {
          errorCode,
          isModalOpen,
        },
      },
    }),
    `@update-errorModal`
  );
}
/**
 * updated error list state
 * @param store - js-atom instance
 * @param errorCode a code used in the component to show different errors
 */
export function setErrorState(
  store: Store,
  key: 'campaignList' | 'userCampaignList',
  errorCode: string
) {
  store.swap(
    (state) => ({
      ...state,
      errors: {
        ...state.errors,
        [key]: errorCode,
      },
    }),
    `@update-errorModal`
  );
}
